<template>
  <span class="ui-chip" :class="`ui-chip--${variant}`" :data-tone="tone ?? undefined">
    <slot />
  </span>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    variant?: "default" | "accent" | "muted" | "warning" | "danger" | "idle" | "active" | "paused" | "blocked";
    tone?: "travel" | "work" | "combat" | "econ" | "social" | null;
  }>(),
  {
    variant: "default",
    tone: null
  }
);
</script>

<style scoped>
.ui-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.6rem;
  border-radius: var(--radius-pill);
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1px solid transparent;
  line-height: 1.2;
  white-space: nowrap;
}

.ui-chip--default {
  background: color-mix(in srgb, var(--accent) 16%, transparent);
  border-color: color-mix(in srgb, var(--accent) 46%, transparent);
}

.ui-chip--accent {
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  border-color: color-mix(in srgb, var(--accent) 52%, transparent);
}

.ui-chip--muted {
  background: var(--chip-muted-bg);
  border-color: var(--chip-muted-border);
  color: var(--fg-1);
}

.ui-chip--warning {
  background: color-mix(in srgb, var(--state-warning) 18%, transparent);
  border-color: color-mix(in srgb, var(--state-warning) 56%, transparent);
}

.ui-chip--danger {
  background: color-mix(in srgb, var(--state-danger) 18%, transparent);
  border-color: color-mix(in srgb, var(--state-danger) 56%, transparent);
}

.ui-chip--idle {
  background: color-mix(in srgb, var(--state-idle) 14%, transparent);
  border-color: color-mix(in srgb, var(--state-idle) 44%, transparent);
  color: var(--fg-1);
}

.ui-chip--active {
  background: color-mix(in srgb, var(--state-active) 16%, transparent);
  border-color: color-mix(in srgb, var(--state-active) 52%, transparent);
}

.ui-chip--paused {
  background: color-mix(in srgb, var(--state-paused) 16%, transparent);
  border-color: color-mix(in srgb, var(--state-paused) 52%, transparent);
}

.ui-chip--blocked {
  background: color-mix(in srgb, var(--state-blocked) 16%, transparent);
  border-color: color-mix(in srgb, var(--state-blocked) 52%, transparent);
}

.ui-chip[data-tone="travel"] {
  --accent: var(--accent-travel);
}

.ui-chip[data-tone="work"] {
  --accent: var(--accent-work);
}

.ui-chip[data-tone="combat"] {
  --accent: var(--accent-combat);
}

.ui-chip[data-tone="econ"] {
  --accent: var(--accent-econ);
}

.ui-chip[data-tone="social"] {
  --accent: var(--accent-social);
}
</style>
